// import { Calendar } from '@/components/ui/calendar';
import { Card, CardContent } from '@/components/ui/card';
import { useEmployees } from '@/hooks/useEmployees';
import { usePayrollSubscription, usePayrollWeek } from '@/hooks/usePayrollWeek';
import { memo, useMemo } from 'react';
import WeeklyHeader from '../SalonWeekly/components/WeeklyHeader';
import { WeeklyPerformanceChart } from '../WeeklyPerformanceChart/WeeklyPerformanceChart';
import EmployeeCard from './components/EmployeeCard';
import { processWeeklyData } from './utils';
import { getWeekRange } from '../SalonWeekly/utils';

interface EmployeeWeeklyProps {
  selectedDate: Date;
  onDateChange: (date: Date) => void;
}

// Loading skeleton component
const LoadingSkeleton = () => (
  <Card>
    <CardContent className="p-6 border-none shadow-none">
      <div className="space-y-4">
        <div className="h-4 animate-pulse bg-muted rounded" />
        <div className="h-32 animate-pulse bg-muted rounded" />
      </div>
    </CardContent>
  </Card>
);

// Main EmployeeWeekly component
const EmployeeWeekly = ({ selectedDate, onDateChange }: EmployeeWeeklyProps) => {
  const { data: payrollData, isLoading, isFetching } = usePayrollWeek(selectedDate);
  const { refetch } = usePayrollWeek(selectedDate);
  usePayrollSubscription(refetch);
  const { employees } = useEmployees();

  // Calculate week range for display using shared function
  const weekRange = useMemo(() => getWeekRange(selectedDate), [selectedDate]);

  // Process weekly data with employee commission rates
  const employeeRecords = useMemo(() => {
    if (!payrollData || !employees) return [];
    return processWeeklyData(payrollData, employees);
  }, [payrollData, employees]);

  // Only skeleton on first load — keep date picker mounted when switching dates
  if ((isLoading && !payrollData) || !payrollData) {
    return <LoadingSkeleton />;
  }

  return (
    <Card className="border-none">
      <CardContent className={`p-2 py-6 sm:p-6${isFetching ? ' opacity-90' : ''}`}>
        <WeeklyHeader
          totalIncome={payrollData.totalIncome}
          weekRange={weekRange}
          date={selectedDate}
          onDateChange={onDateChange}
        />

        {/* Main Content - Split into 2:1 ratio */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          {/* Employee List - Takes 2/3 of the space */}
          <div className="md:col-span-2 space-y-4">
            {[...employeeRecords]
              .sort((a, b) => b.income - a.income)
              .map(record => (
                <EmployeeCard key={record.name} record={record} />
              ))}
          </div>

          {/* Weekly Performance Chart - Takes 1/3 of the space */}
          <div className="md:col-span-1">
            <WeeklyPerformanceChart
              data={payrollData.records.map(day => ({
                day: day.day,
                income: day.income,
              }))}
            />
          </div>
        </div>
      </CardContent>
    </Card>
  );
};

export default memo(EmployeeWeekly);
